import { useEffect } from 'react'
import { Outlet, useParams } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { ProjectService } from 'src/services/project.service'
import CurrentProjectProvider from 'src/providers/current-project.provider'
import { TimezonePreferenceProvider } from 'src/providers/timezone-preference.provider'
import { useNotification } from 'src/providers/notification.provider'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router-dom'
import type { TProject } from 'src/types/project'
import { ECurecallNotificationAppearance } from '@honestica/curecall-ui'
import { useZendesk } from 'react-use-zendesk'
import { useSocket } from 'src/providers/socket.provider'
import { ZendeskService } from 'src/services/zendesk.service'

export const ProjectLayout = () => {
  const { t, i18n } = useTranslation()
  const queryClient = useQueryClient()
  const { notify } = useNotification()
  const [searchParams, setSearchParams] = useSearchParams()
  const { loginUser, setLocale, setConversationFields } = useZendesk()
  const { socket, socketError } = useSocket()

  const { projectId } = useParams()
  const projectIdNum = Number(projectId)
  const locale = i18n.language

  const {
    isLoading: isProjectLoading,
    error: projectError,
    data: project,
  } = useQuery({
    queryKey: ['project', projectIdNum],
    queryFn: () => ProjectService.getById(projectIdNum),
    enabled: !!projectIdNum,
    staleTime: 60 * 60 * 1000,
  })

  useEffect(() => {
    if (project) {
      const authenticateUser = async () => {
        try {
          const token = await ZendeskService.generateJWT()

          loginUser(token, (error) => {
            if (error) {
              console.error('Zendesk authentication failed:', error)
            }
          })

          setConversationFields([
            { id: '33770009772562', value: project.name },
            { id: '33770980616594', value: project.id },
          ])

          // Set widget language
          setLocale(locale)
        } catch (error) {
          console.error('JWT generation failed:', error)
        }
      }

      authenticateUser()

      const previousUserProjectId = searchParams.get('previousUserProjectId')
      if (previousUserProjectId && previousUserProjectId !== String(project.id)) {
        notify({
          title: t('common.actions.success'),
          message: t('pages.projects-page.notification', { name: project.name }),
          appearance: ECurecallNotificationAppearance.SUCCESS,
        })
        // Remove query param
        searchParams.delete('previousUserProjectId')
        setSearchParams(searchParams, { replace: true })
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project, locale])

  // WebSocket subscription - matches Next.js implementation
  useEffect(() => {
    if (!socket || socketError || !project) {
      return
    }

    const handleDeglobalizeProject = () => {
      window.location?.reload()
    }

    const handleUpdateProject = ({ projectId, project }: { projectId: number; project: TProject }) => {
      queryClient.setQueryData(['project', projectId], (oldProject: TProject | undefined) => ({
        ...oldProject,
        ...project,
      }))
    }

    socket.emit('SUBSCRIBE_PROJECT', { projectId: project.id })
    socket.on('DEGLOBALIZED_PROJECT', handleDeglobalizeProject)
    socket.on('PROJECT_SETTINGS_UPDATED', handleUpdateProject)

    return () => {
      socket.emit('UNSUBSCRIBE_PROJECT', { projectId: project.id })
      socket.off('DEGLOBALIZED_PROJECT', handleDeglobalizeProject)
      socket.off('PROJECT_SETTINGS_UPDATED', handleUpdateProject)
    }
  }, [socketError, socket, project, queryClient])

  return (
    <CurrentProjectProvider project={project} isProjectLoading={isProjectLoading} projectError={projectError}>
      <TimezonePreferenceProvider>
        <div className="c-flex c-min-h-screen c-max-h-screen">
          <Outlet />
        </div>
      </TimezonePreferenceProvider>
    </CurrentProjectProvider>
  )
}
